export const MANUAL_CREDIT_OPTION = { value: "manual", label: "Manual / Without Invoice" };
export const normalizeManualReference = (value) => String(value || "").trim().toUpperCase();
export const creditNoteSourceLabel = (note) => note?.source_type === "manual"
  ? `Manual — ${note.manual_reference || "-"}`
  : (note?.source_invoice_numbers || []).join(", ") || "-";

export const validateCreditNoteDraft = ({ sourceType, manualReference, client, company, snapshot, selected, items, isEdit = false }) => {
  if (sourceType === "manual") {
    if (!isEdit && !normalizeManualReference(manualReference)) return "Enter a unique manual reference.";
    if (normalizeManualReference(manualReference).length > 100) return "Manual reference must not exceed 100 characters.";
    if (manualReference && !/^[A-Z0-9][A-Z0-9._/-]{2,99}$/.test(normalizeManualReference(manualReference))) {
      return "Use 3–100 letters, numbers, dots, hyphens, underscores or slashes for the manual reference.";
    }
    if (!isEdit && (!client?.id || !company?.id)) return "Select a client with an active invoicing company.";
    if (!items.length) return "Add at least one manual credit item.";
    if (String(snapshot?.currency_code || "USD").toUpperCase() !== "USD"
      && (!Number.isFinite(Number(snapshot?.exchange_rate)) || Number(snapshot.exchange_rate) <= 0)) {
      return "Enter a valid exchange rate greater than zero.";
    }
  } else if (!selected.length || !items.length) return "Select at least one source invoice and credit item.";
  const invalid = items.find((item) => (
    !Number.isFinite(Number(item.quantity)) || Number(item.quantity) <= 0
    || !Number.isFinite(Number(item.rate)) || Number(item.rate) < 0
    || !Number.isFinite(Number(item.total)) || Number(item.total) <= 0
    || (sourceType === "manual" && !String(item.description || "").trim())
    || (sourceType !== "manual" && (
      Number(item.quantity) > Number(item.remaining_quantity) + 0.01
      || Number(item.total) > Number(item.remaining_total) + 0.01
    ))
  ));
  return invalid ? (sourceType === "manual"
    ? "Every manual item needs a description, a positive quantity and credit amount, and a valid rate."
    : "A credit quantity or amount is invalid or exceeds the remaining invoice item balance.") : "";
};

// Keep the existing linked-invoice payload intact. Manual rows never manufacture
// invoice IDs, so they cannot accidentally reserve or consume invoice balances.
export const creditNoteSourcePayload = ({ sourceType, manualReference, client, company, snapshot, items, canField = () => true }) => (
  sourceType === "manual" ? {
    source_type: "manual",
    ...(manualReference ? { manual_reference: normalizeManualReference(manualReference) } : {}),
    invoice_ids: [],
    ...(client?.id ? { client_id: Number(client.id) } : {}),
    ...(company?.id ? { invoicing_company_id: Number(company.id) } : {}),
    ...(canField("billing_currency") ? { currency_code: snapshot?.currency_code || "USD" } : {}),
    ...(canField("exchange_rate") ? { exchange_rate: String(snapshot?.currency_code || "USD").toUpperCase() === "USD" ? 1 : Number(snapshot?.exchange_rate) } : {}),
    ...(canField("tax") ? { tax_enabled: Boolean(snapshot?.tax_enabled) } : {}),
    items: items.map((item) => ({
      ...(item.id ? { id: Number(item.id) } : {}),
      description: item.description || null,
      project_code: item.project_code || null,
      country: item.country || null,
      quantity: Number(item.quantity), rate: Number(item.rate), total: Number(item.total),
    })),
  } : {
    invoice_ids: Array.from(new Set(items.map((item) => Number(item.source_invoice_id)))),
    items: items.map((item) => ({
      source_invoice_item_id: Number(item.source_invoice_item_id),
      description: item.description || null,
      quantity: Number(item.quantity), rate: Number(item.rate), total: Number(item.total),
    })),
  }
);


// PHP 8.3 round(..., 2) pre-rounding, as used by CreditNoteController.
// This is limited to the new manual preview; linked invoice arithmetic is kept.
export const roundManualCreditMoney = (value) => {
  const number = Number(value) || 0;
  if (number === 0 || !Number.isFinite(number)) return number;
  const magnitude = Math.abs(number);
  const precision = 14 - Math.floor(Math.log10(magnitude));
  let scaled = magnitude * 100;
  if (precision > 2 && precision - 2 < 15) {
    scaled = Math.floor(magnitude * 10 ** precision + 0.5) / 10 ** (precision - 2);
  } else if (scaled >= 1e15) return number;
  return Math.sign(number) * Math.floor(scaled + 0.5) / 100;
};

export const manualCreditNoteAmounts = (items, snapshot) => {
  const source = roundManualCreditMoney(items.reduce((sum, item) => sum + roundManualCreditMoney(item.total), 0));
  const rate = String(snapshot?.currency_code || "USD").toUpperCase() === "USD" ? 1 : Number(snapshot?.exchange_rate || 0);
  const amount = roundManualCreditMoney(source * rate);
  const tax = snapshot?.tax_enabled ? roundManualCreditMoney(amount * Number(snapshot?.tax_percentage || 0) / 100) : 0;
  return { source, amount, tax, total: roundManualCreditMoney(amount + tax) };
};
